import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import {
  TGetSearchErrorData,
  TGetSearchStreamChunk,
  TGetSearchStreamData,
  getSearchStream,
  SuggestionChunk,
  ConvergenceChunk,
  getConvergence
} from "api";
import axios from "axios";
import { CHAT_REPLY_CHUNK_LENGTH } from "../constants";

const defaultData: TGetSearchStreamData = {
  summary: '',
  question: '',
  suggestions: [],
  sources: [],
  convergenceContent: []
}
export const useGetSearchStream = () => {
  const { t } = useTranslation()
  const [data, setData] = useState<TGetSearchStreamData>(defaultData)
  const [error, setError] = useState<TGetSearchErrorData | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const abortControllerRef = useRef<AbortController | null>(null);

  const ErrorMessages: Record<TGetSearchErrorData['status'], string> = {
    204: t('ERROR_GET_SEARCH_204'),
    403: t('ERROR_GET_SEARCH_403'),
    413: t('ERROR_GET_SEARCH_413'),
    429: t('ERROR_GET_SEARCH_429'),
    500: t('ERROR_GET_SEARCH_500'),
  }

  const getErrorMessage = (code: TGetSearchErrorData['status']) => {
    return ErrorMessages[code] ? ErrorMessages[code] : t('ERROR_GET_SEARCH')
  }

  const fetchData = async (search: string, conversationId: string, siteId: string): Promise<void> => {

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setData({ ...defaultData, question: search })
    setError(null)
    setIsLoading(true)

    try {
      const response = await getSearchStream(search, conversationId, siteId, abortControllerRef.current.signal)

      if (response.ok && response.status !== 204 && response.body) {
        const reader = response.body.getReader()
        const decoder = new TextDecoder('utf-8')
        let done = false
        let convergenceData: ConvergenceChunk[] = []
        let contentAccumulator = ''

        while (!done) {
          if (reader) {
            const { value, done: readerDone } = await reader.read()
            done = readerDone

            if (value) {
              const chunk = decoder.decode(value, { stream: true })
              const formattedChunk = `[${chunk.replaceAll('}{', '},{')}]`
              const parsedChunks: TGetSearchStreamChunk[] = JSON.parse(formattedChunk)

              for (const chunk of parsedChunks) {
                switch (chunk.type) {
                  // concat summary
                  case 'answer_chunk':
                    if (chunk.content) {
                      contentAccumulator += chunk.content
                      if (contentAccumulator.length >= CHAT_REPLY_CHUNK_LENGTH) {
                        let summary = contentAccumulator
                        setData((prev) => ({
                          ...prev,
                          summary: `${prev.summary || ''}${summary}`
                        }))
                        contentAccumulator = ''
                      }
                    }
                    break
                  // attach sources
                  case 'answer_end':
                    let summary = contentAccumulator
                    setData((prev) => ({
                      ...prev,
                      summary: `${prev.summary || ''}${summary}`,
                      sources: chunk.metadata.sources
                    }))
                    break
                  // attach suggestions
                  case 'suggestions_end': {
                    const parsed: { questions?: SuggestionChunk[] } = JSON.parse(chunk.content)
                    const suggestionsData = Array.isArray(parsed?.questions) ? parsed.questions : [];
                    if (Array.isArray(suggestionsData) && suggestionsData.length) {
                      setData(prev => ({
                        ...prev,
                        suggestions: suggestionsData.map(v => ({ title: v.question }))
                      }))
                    }
                  }
                    break

                  case "convergence_end": {
                    const parsed: { convergence?: ConvergenceChunk[] } = JSON.parse(chunk.content)
                    convergenceData = Array.isArray(parsed?.convergence) ? parsed.convergence : [];
                  }
                    break
                }
              }
            }
          } else {
            done = true
            setError({
              status: 204,
              message: getErrorMessage(204)
            })
          }
        }

        if (convergenceData.length) {
          const contentIds = convergenceData.map(v => v?.content_id).filter(Boolean)
          if (contentIds.length) {
            const { data: convergenceContent } = await getConvergence(contentIds)
            if (Array.isArray(convergenceContent)) {
              setData(prev => ({ ...prev, convergenceContent }))
            }
          }
        }

      } else {
        setError({
          status: response.status,
          message: getErrorMessage(response.status)
        })
      }
    } catch (error: unknown) {
      const status = axios.isAxiosError(error) && error.response ? error.response.status : 204
      setError({
        status,
        message: getErrorMessage(status)
      })
    } finally {
      setIsLoading(false)
    }
  }

  const resetSearchStreamData = () => {
    setData(defaultData)
  }

  const abortSearchStream = () => {
    abortControllerRef.current?.abort()
  }

  return {
    searchStreamData: data,
    searchStreamError: error,
    isSearchStreamLoading: isLoading,
    fetchSearchStreamData: fetchData,
    resetSearchStreamData,
    abortSearchStream
  }
}